import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Badge, Button, Card, CardBody, Col, Form, FormGroup, Input, Label, Row, Table } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import Switch from 'react-switch';
import { toast } from 'react-toastify';
import LoaderButton from '../loader-button/LoaderButton';
import Heading from '../common/Heading';
import Loader from '../common/Loader';
// services
import BZAPIService from '../../services/BZAPIService';
// libs
import { adminIsReadOnlyCheck } from '../../helpers/rbac';

const Firewall = () => {
  console.log('Firewall component rendered');
  const [isLoading, setIsLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [status, setStatus] = useState('NOT_CONFIGURED');
  const [ipAddresses, setIpAddresses] = useState([]);
  const [newIp, setNewIp] = useState({ ipAddress: '', title: '' });
  const [errors, setErrors] = useState({});

  // Fetch initial data
  useEffect(() => {
    async function loadWafData() {
      try {
        setIsLoading(true);
        const myUserPermissions = await BZAPIService.getMyUserPermissions();
        setIsReadOnly(adminIsReadOnlyCheck(myUserPermissions));
        await Promise.all([fetchStatus(), fetchIpAddresses()]);
      } catch (error) {
        console.error('Error loading WAF data:', error);
        setIsReadOnly(true);
      } finally {
        setIsLoading(false);
      }
    }
    loadWafData();
  }, []);
  // Fetch WAF status
  const fetchStatus = async () => {
    try {
      const response = await BZAPIService.getWafStatus();
      setStatus(response.status);
    } catch (error) {
      console.error('Error fetching WAF status:', error);
      toast.error('Failed to fetch WAF status');
    }
  };

  // Fetch IP addresses
  const fetchIpAddresses = async () => {
    try {
      const response = await BZAPIService.getWafIpAddresses();
      setIpAddresses(response.addresses || []);
    } catch (error) {
      console.error('Error fetching IP addresses:', error);
      toast.error('Failed to fetch IP addresses');
    }
  };

  // Toggle WAF status
  const toggleWafStatus = async () => {
    try {
      setIsLoading(true);
      // Switch checked state represents the desired state, not the current state
      // so we need to check the current status to determine what action to take
      if (status === 'ENABLED') {
        await BZAPIService.disableWaf();
        toast.success('WAF disabled successfully');
      } else {
        if (ipAddresses.length === 0) {
          toast.error('At least one IP address is required to enable WAF');
          setIsLoading(false);
          return;
        }
        await BZAPIService.enableWaf();
        toast.success('WAF enabled successfully');
      }
      await fetchStatus();
    } catch (error) {
      console.error('Error toggling WAF status:', error);
      toast.error(`Failed to ${status === 'ENABLED' ? 'disable' : 'enable'} WAF: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const validateIpForm = ipData => {
    const errors = {};
    if (!ipData.ipAddress) {
      errors.ipAddress = 'IP address is required';
    } else if (!/^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/.test(ipData.ipAddress)) {
      errors.ipAddress = 'Invalid IP address format';
    }
    if (!ipData.title) {
      errors.title = 'Label is required';
    }
    return errors;
  };

  // Validates and adds a new IP address to the WAF whitelist.
  // Requires IPv4 format with optional CIDR notation (/32).
  const handleAddIp = async e => {
    e.preventDefault();
    const validationErrors = validateIpForm(newIp);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsLoading(true);
      await BZAPIService.addWafIpAddress(newIp);
      await fetchIpAddresses();
      setNewIp({ ipAddress: '', title: '' });
      setErrors({});
      toast.success('IP address added successfully');
    } catch (error) {
      console.error('Error adding IP:', error);
      toast.error('Failed to add IP address');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete IP
  const handleDeleteIp = useCallback(async ipAddress => {
    try {
      setIsLoading(true);
      await BZAPIService.deleteWafIpAddress(ipAddress);
      await fetchIpAddresses();
      toast.success('IP address deleted successfully');
    } catch (error) {
      console.error('Error deleting IP:', error);
      toast.error('Failed to delete IP address');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getStatusBadgeColor = () => {
    switch (status) {
      case 'ENABLED':
        return 'success';
      case 'DISABLED':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  if (isLoading && ipAddresses.length === 0) return <Loader text="Loading firewall rules..." />;

  return (
    <>
      <Heading
        title="WAF IP Whitelist"
        subtitle="Manage your WAF IP whitelist configuration"
        className="mb-4 mt-3"
        icon="shield"
      />
      {isReadOnly && (
        <Alert color="info">Note: Your user only has read-only access. You will not be able to save any updates.</Alert>
      )}

      <Card>
        <CardBody className="fs--1">
          <Row className="align-items-center mb-4">
            <Col>
              <h5 className="mb-0">WAF Status</h5>
            </Col>
            <Col xs="auto" className="d-flex align-items-center">
              <Badge color={getStatusBadgeColor()} className="me-3" pill>
                {status}
              </Badge>
              <div className="d-flex align-items-center">
                {/* <span className="me-2">{status !== 'ENABLED' ? 'Disabled' : 'Enabled'}</span> */}
                {isLoading ? (
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <Switch
                    checked={status === 'ENABLED'}
                    onChange={toggleWafStatus}
                    disabled={(status === 'NOT_CONFIGURED' && ipAddresses.length === 0) || isLoading || isReadOnly}
                    onColor="#86d3ff"
                    onHandleColor="#2693e6"
                    handleDiameter={24}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                    activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                    height={20}
                    width={48}
                    className="ms-2"
                  />
                )}
              </div>
            </Col>
          </Row>

          <Form onSubmit={handleAddIp} className="mb-4">
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="ipAddress">IP Address</Label>
                  <Input
                    type="text"
                    id="ipAddress"
                    placeholder="e.g., 192.168.1.1"
                    value={newIp.ipAddress}
                    onChange={e => setNewIp({ ...newIp, ipAddress: e.target.value })}
                    invalid={!!errors.ipAddress}
                  />
                  <div style={{ minHeight: '24px' }}>
                    {errors.ipAddress && <div className="invalid-feedback d-block">{errors.ipAddress}</div>}
                  </div>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="title">Label</Label>
                  <Input
                    type="text"
                    id="title"
                    placeholder="e.g., Office Network"
                    value={newIp.title}
                    onChange={e => setNewIp({ ...newIp, title: e.target.value })}
                    invalid={!!errors.title}
                  />
                  <div style={{ minHeight: '24px' }}>
                    {errors.title && <div className="invalid-feedback d-block">{errors.title}</div>}
                  </div>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label className="invisible">Action</Label>
                  <LoaderButton
                    type="submit"
                    color="primary"
                    loading={isLoading}
                    disabled={isReadOnly}
                    className="w-100"
                  >
                    Add IP Address
                  </LoaderButton>
                </FormGroup>
              </Col>
            </Row>
          </Form>

          <Table responsive bordered striped>
            <thead>
              <tr>
                <th>IP Address</th>
                <th>Label</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {ipAddresses.map(ip => (
                <tr key={ip.ipAddress}>
                  <td>{ip.ipAddress}</td>
                  <td>{ip.title}</td>
                  <td className="text-end">
                    <Button
                      color="falcon-danger"
                      size="sm"
                      onClick={() => handleDeleteIp(ip.ipAddress)}
                      disabled={isLoading || isReadOnly}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </Button>
                  </td>
                </tr>
              ))}
              {ipAddresses.length === 0 && (
                <tr>
                  <td colSpan="3" className="text-center">
                    No IP addresses configured
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </>
  );
};

export default Firewall;
